import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { User } from '@/api/types';

/** Auth state for the admin dashboard. Tokens persisted to localStorage so
 * a refresh keeps the session alive — admins are spending real time in here
 * and shouldn't have to re-log every page reload. */
interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  setAuth: (user: User, access: string, refresh: string) => void;
  setUser: (user: User) => void;
  setTokens: (access: string, refresh: string) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      setAuth: (user, accessToken, refreshToken) =>
        set({ user, accessToken, refreshToken }),
      setUser: (user) => set({ user }),
      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),
      clear: () => set({ user: null, accessToken: null, refreshToken: null }),
    }),
    {
      name: 'offcoder-admin.auth',
      // Skip rehydrating non-persistable methods (Zustand handles this
      // automatically for actions, but listing partialize is explicit).
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    },
  ),
);
